import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Button, Modal, Form, Alert, Badge, Row, Col } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';
import { fmtMoney, fmtDate } from '../lib/format.js';

const PAYMENT_METHODS = [
  { value: '',                label: '—' },
  { value: 'bank_transfer',   label: 'Überweisung' },
  { value: 'sepa',            label: 'SEPA-Lastschrift' },
  { value: 'credit_card',     label: 'Kreditkarte' },
  { value: 'cash',            label: 'Bar' },
  { value: 'paypal',          label: 'PayPal' },
  { value: 'other',           label: 'Sonstige' }
];

const EMPTY_DRAFT = {
  received_at: new Date().toISOString().slice(0, 10),
  donor_name: '',
  donor_email: '',
  donor_address: '',
  amount: '',
  currency: 'EUR',
  payment_method: '',
  purpose: '',
  note: ''
};

export default function DonationsPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const canWrite = auth.can('donations', 'create');
  const canDelete = auth.can('donations', 'delete');

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [donorFilter, setDonorFilter] = useState('');
  const [receiptFilter, setReceiptFilter] = useState('');

  const donations = useQuery({
    queryKey: ['donations', year, donorFilter, receiptFilter],
    queryFn: () => api.get('/donations', { params: {
      limit: 1000,
      year,
      donor: donorFilter || undefined,
      receipt_issued: receiptFilter === '' ? undefined : receiptFilter
    } }).then(r => r.data.items)
  });
  const summary = useQuery({
    queryKey: ['donation-summary', year],
    queryFn: () => api.get('/donations/summary', { params: { year } }).then(r => r.data.summary)
  });

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [error, setError] = useState(null);

  const open = (d = null) => {
    setError(null);
    if (d) {
      setEditing(d);
      setDraft({
        received_at: d.received_at,
        donor_name: d.donor_name,
        donor_email: d.donor_email || '',
        donor_address: d.donor_address || '',
        amount: (d.amount_cents / 100).toFixed(2).replace('.', ','),
        currency: d.currency,
        payment_method: d.payment_method || '',
        purpose: d.purpose || '',
        note: d.note || ''
      });
    } else {
      setEditing(null);
      setDraft(EMPTY_DRAFT);
    }
    setShowModal(true);
  };
  const close = () => setShowModal(false);
  const set = (k) => (e) => setDraft(d => ({ ...d, [k]: e.target.value }));

  const buildBody = () => {
    const cents = Math.round(parseFloat(String(draft.amount).replace(',', '.')) * 100);
    return {
      received_at: draft.received_at,
      donor_name: draft.donor_name,
      donor_email: draft.donor_email || null,
      donor_address: draft.donor_address || null,
      amount_cents: cents,
      currency: draft.currency || 'EUR',
      payment_method: draft.payment_method || null,
      purpose: draft.purpose || null,
      note: draft.note || null
    };
  };

  const save = useMutation({
    mutationFn: () => editing
      ? api.patch(`/donations/${editing.id}`, buildBody()).then(r => r.data)
      : api.post('/donations', buildBody()).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['donations'] });
      qc.invalidateQueries({ queryKey: ['donation-summary'] });
      qc.invalidateQueries({ queryKey: ['finance-summary'] });
      close();
    },
    onError: (e) => setError(e.response?.data?.message || JSON.stringify(e.response?.data?.details || e.response?.data))
  });

  const issueReceipt = useMutation({
    // Quittungsnummer wird automatisch generiert (Format: SQ-YYYY-NNNN)
    mutationFn: (id) => api.post(`/donations/${id}/receipt`, {}).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['donations'] });
      qc.invalidateQueries({ queryKey: ['donation-summary'] });
    },
    onError: (e) => alert(e.response?.data?.message || e.message)
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/donations/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['donations'] });
      qc.invalidateQueries({ queryKey: ['donation-summary'] });
      qc.invalidateQueries({ queryKey: ['finance-summary'] });
    },
    onError: (e) => alert(e.response?.data?.message || e.message)
  });

  const sum = summary.data || { total: 0, with_receipt: 0, without_receipt: 0, count: 0 };
  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) years.push(y);

  return (
    <>
      <PageHeader title="Spenden / Zuwendungen"
        subtitle="Geldzuwendungen ohne Gegenleistung — Spendenquittungen optional"
        actions={canWrite && (
          <Button onClick={() => open(null)}>
            <Icon icon="tabler:plus" className="me-1" />Neue Spende
          </Button>
        )} />

      <Row className="g-3 mb-3">
        <Col md={4}><Card className="card-flat"><Card.Body>
          <div className="text-muted small text-uppercase">Gesamt {year === 'all' ? '(alle Jahre)' : year}</div>
          <div className="fs-4 fw-semibold text-success">{fmtMoney(sum.total)}</div>
          <div className="text-muted small">{sum.count} Spende{sum.count === 1 ? '' : 'n'}</div>
        </Card.Body></Card></Col>
        <Col md={4}><Card className="card-flat"><Card.Body>
          <div className="text-muted small text-uppercase">Mit Spendenquittung</div>
          <div className="fs-4 fw-semibold">{fmtMoney(sum.with_receipt)}</div>
        </Card.Body></Card></Col>
        <Col md={4}><Card className="card-flat"><Card.Body>
          <div className="text-muted small text-uppercase">Ohne Spendenquittung</div>
          <div className={`fs-4 fw-semibold ${sum.without_receipt > 0 ? 'text-warning' : ''}`}>{fmtMoney(sum.without_receipt)}</div>
        </Card.Body></Card></Col>
      </Row>

      <div className="d-flex gap-2 flex-wrap mb-3">
        <Form.Select size="sm" style={{ width: 120 }} value={year}
          onChange={(e) => setYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))}>
          <option value="all">Gesamt</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </Form.Select>
        <Form.Control size="sm" style={{ width: 220 }} placeholder="Spender filtern…"
          value={donorFilter} onChange={(e) => setDonorFilter(e.target.value)} />
        <Form.Select size="sm" style={{ width: 200 }} value={receiptFilter} onChange={(e) => setReceiptFilter(e.target.value)}>
          <option value="">Alle Quittungs-Status</option>
          <option value="true">Mit Quittung</option>
          <option value="false">Ohne Quittung</option>
        </Form.Select>
      </div>

      <Card className="card-flat">
        <Card.Body className="p-0">
          {donations.isLoading ? (
            <div className="text-center text-muted p-4"><div className="spinner-border spinner-border-sm me-2" />Lädt…</div>
          ) : (donations.data || []).length === 0 ? (
            <div className="text-center text-muted p-4">
              <Icon icon="tabler:gift-off" style={{ fontSize: 48 }} className="d-block mb-2 opacity-50" />
              <div>Keine Spenden {year === 'all' ? 'erfasst' : `in ${year}`}.</div>
              {canWrite && <Button variant="link" onClick={() => open(null)}>Erste Spende erfassen</Button>}
            </div>
          ) : (
            <Table hover responsive className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Beleg-Nr.</th>
                  <th>Datum</th>
                  <th>Spender</th>
                  <th>Zweck</th>
                  <th className="text-end">Betrag</th>
                  <th>Quittung</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(donations.data || []).map(d => (
                  <tr key={d.id} style={{ cursor: canWrite ? 'pointer' : 'default' }}>
                    <td onClick={() => canWrite && open(d)}><code className="small">{d.donation_number}</code></td>
                    <td onClick={() => canWrite && open(d)}>{fmtDate(d.received_at)}</td>
                    <td onClick={() => canWrite && open(d)}>
                      <div>{d.donor_name}</div>
                      {d.donor_email && <div className="text-muted small">{d.donor_email}</div>}
                    </td>
                    <td onClick={() => canWrite && open(d)} className="text-muted small">{d.purpose || '—'}</td>
                    <td className="text-end fw-semibold" onClick={() => canWrite && open(d)}>
                      {fmtMoney(d.amount_cents, d.currency)}
                    </td>
                    <td>
                      {d.receipt_issued
                        ? <Badge bg="" className="badge-soft-success">
                            {d.receipt_number ? `Nr. ${d.receipt_number}` : 'Ausgestellt'}
                          </Badge>
                        : <Badge bg="" className="badge-soft-warning">Ausstehend</Badge>}
                    </td>
                    <td className="text-end pe-3" onClick={(e) => e.stopPropagation()}>
                      {!d.receipt_issued && canWrite && (
                        <Button size="sm" variant="outline-success" className="me-1"
                          title="Spendenquittung ausgestellt" onClick={() => issueReceipt.mutate(d.id)}>
                          <Icon icon="tabler:receipt" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="sm" variant="outline-danger"
                          title="Löschen" onClick={() => {
                            if (confirm(`Spende ${d.donation_number} (${d.donor_name}, ${fmtMoney(d.amount_cents)}) wirklich löschen?`)) del.mutate(d.id);
                          }}>
                          <Icon icon="tabler:trash" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={close} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? `Spende ${editing.donation_number} bearbeiten` : 'Neue Spende'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Datum</Form.Label>
                <Form.Control type="date" value={draft.received_at} onChange={set('received_at')} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Betrag</Form.Label>
                <Form.Control type="text" inputMode="decimal" placeholder="z.B. 50,00"
                  value={draft.amount} onChange={set('amount')} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Zahlungsart</Form.Label>
                <Form.Select value={draft.payment_method} onChange={set('payment_method')}>
                  {PAYMENT_METHODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Spender (Name)</Form.Label>
                <Form.Control type="text" value={draft.donor_name} onChange={set('donor_name')} required />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>E-Mail (optional)</Form.Label>
                <Form.Control type="email" value={draft.donor_email} onChange={set('donor_email')} />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Anschrift (für Spendenquittung)</Form.Label>
                <Form.Control as="textarea" rows={2} value={draft.donor_address} onChange={set('donor_address')}
                  placeholder="Straße, PLZ Ort" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Zweck / Projekt</Form.Label>
                <Form.Control type="text" value={draft.purpose} onChange={set('purpose')}
                  placeholder="z.B. Drohnenkauf, allgemeine Vereinsarbeit" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Notiz</Form.Label>
                <Form.Control type="text" value={draft.note} onChange={set('note')} />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={close}>Abbrechen</Button>
          <Button onClick={() => save.mutate()} disabled={save.isLoading || !draft.donor_name || !draft.amount}>
            {save.isLoading ? 'Speichert…' : (editing ? 'Speichern' : 'Anlegen')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
